import React, { type ReactNode } from "react";
import cx from "classnames";
import { useIsClient } from "@/hooks/useIsClient";
import { Toaster, useToast } from "@picklebet/ui";
import { useDisplayAtMost } from "@/hooks/useDisplay";
import useLockScroll from "@/hooks/useLockScroll";
import { isAndroidWebView } from "@/utilities/display";
import { triggerHaptic } from "@/utilities/haptics";
import { FloatingStreamPlayer } from "@/features/stream/components/FloatingStreamPlayer";
import { useShellStoreEffects } from "../hooks/useShellStoreEffects";
import { useDockContent } from "../hooks/useDock";
import { useShellStore } from "../stores/shellStore";
import type { NavLocation, ShellOptions } from "../types";
import { AppShell } from "./AppShell";
import { ClientOnly } from "./ClientOnly";
import { Footer } from "./Footer/Footer";
import { GlobalOverlays } from "./GlobalOverlays";
import { NavRail } from "./NavRail";
import { ShellBottomNav } from "./ShellBottomNav";
import { ShellDock } from "./ShellDock";
import { ShellNavDrawer } from "./ShellNavDrawer";
import { ShellTopBar } from "./ShellTopBar";
import { StatusMessage } from "./StatusMessage";

import "styles/core.scss";
import * as styles from "./Shell.module.scss";

type ShellProps = {
  location: NavLocation;
  options: ShellOptions;
  children: ReactNode;
};

const ShellToasts = ({ isDockOverlaying }: { isDockOverlaying: boolean }) => {
  const menuOpen = useShellStore((state) => state.menuOpen);
  const { isLowPriority } = useToast();

  return (
    <AppShell.ToastAnchor
      className={cx({
        [styles.lowPriority]: isLowPriority,
        [styles.underOverlay]: menuOpen || isDockOverlaying,
      })}
    >
      <Toaster
        haptics={{
          success: triggerHaptic.success,
          danger: triggerHaptic.error,
        }}
      />
    </AppShell.ToastAnchor>
  );
};

export const Shell = ({ location, options, children }: ShellProps) => {
  useShellStoreEffects(location.pathname, options.dock);

  const dockOpen = useShellStore((state) => state.dockOpen);
  const keyboardOpen = useShellStore((state) => state.keyboardOpen);
  const dockContent = useDockContent();
  const isDockCollapsible = useDisplayAtMost("desktop");
  useLockScroll(dockOpen && isDockCollapsible);
  const isDockOverlaying = dockOpen && isDockCollapsible;
  const isAndroidApp = useIsClient() && isAndroidWebView();

  return (
    <>
      <AppShell.Root
        data-dock-open={dockOpen}
        data-dock-content={dockContent}
        data-keyboard-open={keyboardOpen}
        data-android-app={isAndroidApp}
      >
        <AppShell.Stream>
          <FloatingStreamPlayer />
        </AppShell.Stream>
        <AppShell.Status>
          <StatusMessage />
        </AppShell.Status>
        <AppShell.Body>
          <NavRail location={location} inert={isDockOverlaying} />
          <AppShell.Main inert={isDockOverlaying}>
            <ShellTopBar pathname={location.pathname} options={options} />
            <AppShell.Content>{children}</AppShell.Content>
          </AppShell.Main>
          {dockContent !== "none" && (
            <ShellDock content={dockContent} isOpen={dockOpen} />
          )}
        </AppShell.Body>
        <AppShell.Footer inert={isDockOverlaying}>
          <Footer />
        </AppShell.Footer>
        <ShellBottomNav location={location} />
        <ShellToasts isDockOverlaying={isDockOverlaying} />
      </AppShell.Root>
      <ShellNavDrawer location={location} />
      <ClientOnly>
        <GlobalOverlays />
      </ClientOnly>
    </>
  );
};
